// The outbox (REQ-OFF-02): every change made in the field is stored on the phone with a unique
// operation ID and sent in recorded order when there is signal; the server applies it exactly once.
import type { ActionCode } from "@rk/shared";
import { db, type FileBlob, type OutboxOp } from "./db";
import { newId } from "../lib/ids";
import { recordClientError } from "../lib/errors";

export async function enqueue(action: ActionCode, payload: unknown, opts: { tempRef?: string | undefined } = {}): Promise<OutboxOp> {
  const op: OutboxOp = {
    operation_id: newId(),
    action,
    payload,
    recorded_at: new Date().toISOString(),
    status: "waiting",
    temp_ref: opts.tempRef,
    attempts: 0
  };
  await db.outbox.put(op);
  window.dispatchEvent(new CustomEvent("rk:outbox-changed"));
  return op;
}

/**
 * Stores a photo, voice note or file on the phone; it uploads in the background at sync (REQ-OFF-06). The bytes are
 * read at once, before anything else is awaited, and kept as an ArrayBuffer (Decision 84 #1: WebKit can refuse to store
 * a Blob, and a picked file can become unreadable once the page is in the background). Throws when it cannot be kept.
 */
export async function stashFile(blob: Blob, filename: string, source: "camera" | "upload" | "voice", extra: { doc_type?: string; transcript?: string; upload_batch?: string; id?: string } = {}): Promise<string> {
  return keepBytes(readBytes(blob), blob, filename, source, extra);
}

/** Starts reading a file's bytes now (call it before any await, while the picked file can still be read). */
export function readBytes(blob: Blob): Promise<ArrayBuffer> {
  try {
    return typeof blob.arrayBuffer === "function" ? blob.arrayBuffer() : new Response(blob).arrayBuffer();
  } catch (e) {
    return Promise.reject(e);
  }
}

/**
 * Keeps bytes already being read (see stashFile) as a file waiting to upload; returns its "file:<id>" reference. The bytes
 * go to db.fileBytes and the file's record (name, type, size) to db.files, in one transaction, bytes first: a page closed
 * part-way leaves neither, so the pick is named as never kept (Decision 84 #1 round 2, F4 a).
 */
export async function keepBytes(bytes: Promise<ArrayBuffer>, blob: Blob, filename: string, source: string, extra: { doc_type?: string; transcript?: string; upload_batch?: string; id?: string } = {}): Promise<string> {
  const { id = newId(), ...rest } = extra;
  const data = await bytes;
  await db.transaction("rw", db.fileBytes, db.files, async () => {
    await db.fileBytes.put({ id, filename, data });
    await db.files.put({ id, operation_id: newId(), filename, mime: blob.type || "application/octet-stream", source, uploaded: false, size: data.byteLength, ...rest });
  });
  return `file:${id}`;
}

/** A kept file's bytes, read only to upload it: from db.fileBytes, or from the row itself (the older shape). */
export async function fileBytesOf(f: FileBlob): Promise<Blob | ArrayBuffer | null> {
  const row = await db.fileBytes.get(f.id);
  return row?.data ?? f.blob ?? null;
}

/** A file's size in bytes, without loading its bytes (rows of the older shape carry them anyway). */
export function fileSize(f: FileBlob): number {
  if (typeof f.size === "number") return f.size;
  const b = f.blob;
  return b instanceof Blob ? b.size : b ? (b as ArrayBuffer).byteLength : 0;
}

/** What the person is told about a file that could not be kept on the phone (Decision 84 #1). */
export function notKeptReason(name: string): string {
  return `"${name}" could not be kept on this phone. Pick it again.`;
}

/** The error's own name (UnknownError, NotReadableError…), also when the store wrapped it. */
export function errorName(e: unknown): string {
  const inner = (e as { inner?: { name?: string } } | null)?.inner?.name;
  return inner || (e instanceof Error || e instanceof DOMException ? e.name : "Error");
}

/** Logs a file that could not be kept, with its name, size, type and the error (Decision 84 #1). */
export function reportNotKept(name: string, file: Blob, e: unknown) {
  const error = errorName(e);
  recordClientError(`A picked file could not be kept on this phone (${error}): ${e instanceof Error || e instanceof DOMException ? e.message : String(e)}`,
    { filename: name, size: file.size, mime: file.type || "application/octet-stream", error });
}

export async function waitingOps(): Promise<OutboxOp[]> {
  return db.outbox.where("status").anyOf("waiting", "sending").sortBy("recorded_at");
}

// Letters only (no I or O, so they are not read as 1 or 0): the prefix never joins the counter into a long digit run.
const LETTERS = "ABCDEFGHJKLMNPQRSTUVWXYZ";
// Six random letters per page load (from crypto, not Math.random) and a counter: the "temp-ids" map in offline/sync
// outlives the page, so a number must not repeat after a reload either (CI release run 221).
const PAGE = Array.from(newId().replace(/-/g, "").slice(0, 12).match(/../g)!, (b) => LETTERS[parseInt(b, 16) % LETTERS.length]).join("");
let count = 0;

/** A temporary number shown until the server assigns the final ID at sync (REQ-OFF-04), e.g. TEMP-WBK-KXQRTM-3. */
export function tempAnimalId(speciesCode: string | null | undefined): string {
  return `TEMP-${speciesCode ?? "ANI"}-${PAGE}-${++count}`;
}
